import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import useToast from '../contexts/useToast';
import { verifyCheckoutSession } from '../stripe';

type UpgradeTier = 'plus' | 'pro';

const SubCallback = () => {
  const { showToast } = useToast();
  const navigate = useNavigate();
  const [status, setStatus] = useState<'loading' | 'success'>('loading');
  const [tier, setTier] = useState<UpgradeTier>('plus');

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const returnTo = params.get('returnTo') || '/';
    const wasPlaying = params.get('wasPlaying') === 'true';
    const sessionId = params.get('session_id');
    const requestedTier = (params.get('tier') as UpgradeTier) || 'plus';
    setTier(requestedTier);

    // Build final URL, adding isPlaying if user was playing when prompted
    const finalUrl = wasPlaying ? `${returnTo}?isPlaying=true` : returnTo;

    const processUpgrade = async () => {
      // If upgraded=true, this is an in-place subscription upgrade (no Stripe checkout)
      const upgraded = params.get('upgraded') === 'true';
      if (upgraded) {
        setStatus('success');
        setTimeout(() => {
          navigate(finalUrl, { replace: true });
        }, 3000);
        return;
      }

      // If we have a session_id from Stripe, verify it
      if (sessionId) {
        const result = await verifyCheckoutSession(sessionId);
        if (result.verified) {
          if (result.tier) setTier(result.tier as UpgradeTier);
          setStatus('success');
          setTimeout(() => {
            navigate(finalUrl, { replace: true });
          }, 3000);
          return;
        } else {
          // Session not verified, but webhook might have processed it
          // Check user's tier by refreshing, then show success anyway
          setStatus('success');
          setTimeout(() => {
            navigate(finalUrl, { replace: true });
          }, 3000);
          return;
        }
      }

      // Fallback: No session_id means gift/test mode - use the upgrade endpoint
      const endpoint = `/api/upgrade`;
      try {
        const response = await fetch(endpoint, {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ tier: requestedTier }),
        });

        if (response.ok) {
          setStatus('success');
          // Show success message for 3 seconds before redirecting
          setTimeout(() => {
            navigate(finalUrl, { replace: true });
          }, 3000);
        } else {
          showToast('Upgrade failed. Please try again.', 'error');
          navigate('/', { replace: true });
        }
      } catch (error) {
        console.error('Subscription error:', error);
        showToast('Upgrade failed. Please try again.', 'error');
        navigate('/', { replace: true });
      }
    };

    processUpgrade();
  }, [showToast, navigate]);

  const isPro = tier === 'pro';

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        height: '100vh',
        textAlign: 'center',
        padding: '2rem',
      }}
    >
      {status === 'loading' ? (
        <p style={{ color: 'var(--primary)' }}>
          {isPro ? 'Upgrading to Pro…' : 'Upgrading to Plus…'}
        </p>
      ) : (
        <>
          <h2
            style={{
              color: 'var(--teal)',
              marginBottom: '1rem',
              fontSize: '1.5rem',
            }}
          >
            {isPro ? 'Welcome to Pro' : 'Welcome to Plus'}
          </h2>
          <p style={{ color: 'var(--primary)', marginBottom: '1rem' }}>
            {isPro
              ? 'Unlimited listening + PDF parsing (beta) are now unlocked.'
              : 'You now have 20 hours of listening per month.'}
          </p>
          <p style={{ color: 'var(--primary)', opacity: 0.7 }}>Redirecting…</p>
        </>
      )}
    </div>
  );
};

export default SubCallback;
